import { useEffect, useState } from "react";
import ReactExport from "react-export-excel";
import { Grid, Button, Divider, IconButton, Tooltip } from "@material-ui/core";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import TableNotasFiscais from "../components/table-notas-fiscais";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import TableChartIcon from "@mui/icons-material/TableChart";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const statusOptions = [
  { id: "todos", Nome: "Todos" },
  { id: "nf_cancelada", Nome: "NF Cancelada" },
  { id: "nf_devolvida_parcialmente", Nome: "NF Devolvida Parcialmente" },
  { id: "nf_devolvida_totalmente", Nome: "NF Devolvida Totalmente" },
  { id: "nf_emitida", Nome: "NF Emitida" },
];

const statusMap = {
  todos: null,
  nf_cancelada: "Nf cancelada",
  nf_devolvida_parcialmente: "nf devolvida parcialmente",
  nf_devolvida_totalmente: "nf devolvida totalmente",
  nf_emitida: "nf emitida",
};

const ExportComponent = ({ data, dataInicial, dataFinal, loading }) => {
  return (
    <ExcelFile
      element={
        <Tooltip title="Exportar Excel">
          <IconButton size="small" disabled={loading} color="gray">
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      filename={`notas-fiscais-${dataInicial || ""}-${dataFinal || ""}`}
    >
      <ExcelSheet data={data} name="Notas Fiscais">
        <ExcelColumn label="Número" value="NumeroNf" />
        <ExcelColumn label="Data Emissão" value="DataEmissao" />
        <ExcelColumn label="Status" value="Status" />
        <ExcelColumn label="Valor Líquido" value="ValorTotal" />
        <ExcelColumn label="Entregue" value="Entregue" />
        <ExcelColumn label="Previsão Entrega" value="DataPrevisaoEntrega" />
        <ExcelColumn label="Proposta" value="NrProposta" />
        <ExcelColumn label="Pedido" value="NrPedido" />
        <ExcelColumn label="Marca" value="Marca" />
        <ExcelColumn label="CC Loja" value="CCLoja" />
        <ExcelColumn label="Condição Pagamento" value="CondicaoPagamento" />
      </ExcelSheet>
    </ExcelFile>
  );
}

export default function NotasFiscais({
  structureFilters,
  cnpj = "",
  dataInicial,
  dataFinal,
}) {
  const [status, setStatus] = useState(statusOptions[0]);
  const [data, setData] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();
  const hasStructure =
    structureFilters?.idGrupoEconomico || structureFilters?.idLoja?.length;

  const handleFilter = async () => {
    if (!hasStructure && !cnpj) {
      snackbar.showMessage(
        "Selecione Grupo econômico e/ou Lojas e/ou Marcas e/ou Linha OU informe CNPJ e/ou Marca e/ou Linha",
        { severity: "error" }
      );
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        idMarca: structureFilters?.idMarca || "TODAS",
        idGrupoEconomico: structureFilters?.idGrupoEconomico || "",
        idLoja: structureFilters?.idLoja?.length ? structureFilters.idLoja : "",
        cnpj: structureFilters?.cnpj?.replace(/\D/g, "") || "",
        dtIni: dataInicial,
        dtFim: dataFinal,
        status: statusMap[status?.id] || "TODOS",
      });

      [...params.entries()]
        .filter(
          ([, value]) => value === undefined || value === null || value === ""
        )
        .forEach(([key]) => params.delete(key));

      const response = await Api.get(
        "visao360-notas-fiscais",
        "reportApp",
        `?${params.toString()}`,
        true
      );

      const responseData = response?.Result || response;

      const parsePrice = (value) => {
        if (!value) return null;
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          const cleaned = value.replace(/\./g, "").replace(",", ".");
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      };

      // Mapear os campos da API para os campos esperados pela tabela
      const mappedNotasFiscais = (responseData.NotasFiscais || []).map((item) => {
        const valorLiquido = parsePrice(item.ValorLiquido);
        const valorTotal = parsePrice(item.ValorTotal);
        
        return {
          ...item,
          // Mapear campos que podem vir com nomes diferentes
          NumeroNf: item.NumeroNf || item.Numero,
          DataEmissao: item.DataEmissao || item.DataEmissaoFormat,
          Status: item.Status || item.StatusNf,
          ValorTotal: valorLiquido || valorTotal,
          ValorLiquido: valorLiquido || valorTotal,
          DataPrevisaoEntrega: item.DataPrevisaoEntrega || item.DataPrevisaoEntregaFormat,
          NrProposta: item.NrProposta ?? item.NumeroProposta,
          NrPedido: item.NrPedido ?? item.NumeroPedido,
          CCLoja: item.CCLoja || item.CcLoja || item.NomeCc,
          CondicaoPagamento:
            item.CondicaoPagamento || item.NomeCondicaoPgto,
          ChaveNfe: item.ChaveNfe || item.ChaveNf,
          Entregue: item.Entregue === true ? "Sim" : item.Entregue === false ? "Não" : item.Entregue,
        };
      });

      setData(mappedNotasFiscais);
      setTotalRegistros(responseData.TotalRegistros);
    } catch (err) {
      snackbar.showMessage(
        err.message || "Erro ao carregar dados de notas fiscais",
        {
          severity: "error",
        }
      );
      setData([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFilter();
  }, []);

  return (
    <>
      <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
        <Grid item xs={12} md={4} lg={3}>
          <AutoCompleteField
            label="Status"
            options={statusOptions}
            value={status}
            onChange={setStatus}
            optionLabelKey="Nome"
            optionIdKey="id"
          />
        </Grid>
        <Grid item xs={12} md={1} lg={1} style={{ marginTop: "1.25rem" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleFilter({})}
            disabled={loading}
          >
            Filtrar
          </Button>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} style={{ margin: "1rem 0" }}>
          <Divider />
        </Grid>
      </Grid>

      {loading && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LoadingData searching label="Buscando dados de notas fiscais..." />
          </Grid>
        </Grid>
      )}

      {!loading && (
        <TableNotasFiscais
          data={data}
          totalRegistros={totalRegistros}
          loading={loading}
          showPrint
          extraActions={<ExportComponent data={data} dataInicial={dataInicial} dataFinal={dataFinal} loading={loading} />}
        />
      )}
    </>
  );
}
